type AuditActionType =
  | "created"
  | "alert_sent"
  | "alert_acknowledged"
  | "status_change"
  | "document_uploaded"
  | "note_added"
  | "escalation"
  | "workflow_started"
  | "workflow_step_completed"
  | "workflow_step_skipped"
  | "workflow_step_reopened"
  | "workflow_step_overdue"
  | "workflow_completed"
  | "renewal_decision";

interface AuditEntry {
  id: string;
  contractId: number;
  action: string;
  actionType: AuditActionType;
  performedBy: string;
  notes: string;
  createdAt: string;
}

const actionConfig: Record<AuditActionType, {
  icon: string;
  bg: string;
  iconColor: string;
  dotColor: string;
}> = {
  created: {
    icon: "ri-add-circle-line",
    bg: "bg-accent-50",
    iconColor: "text-accent-500",
    dotColor: "bg-accent-500",
  },
  alert_sent: {
    icon: "ri-notification-3-line",
    bg: "bg-primary-50",
    iconColor: "text-primary-500",
    dotColor: "bg-primary-500",
  },
  alert_acknowledged: {
    icon: "ri-check-double-line",
    bg: "bg-secondary-50",
    iconColor: "text-secondary-500",
    dotColor: "bg-secondary-500",
  },
  status_change: {
    icon: "ri-arrow-left-right-line",
    bg: "bg-accent-50",
    iconColor: "text-accent-600",
    dotColor: "bg-accent-600",
  },
  document_uploaded: {
    icon: "ri-file-upload-line",
    bg: "bg-background-100",
    iconColor: "text-foreground-500",
    dotColor: "bg-foreground-400",
  },
  note_added: {
    icon: "ri-sticky-note-line",
    bg: "bg-background-100",
    iconColor: "text-foreground-400",
    dotColor: "bg-foreground-300",
  },
  escalation: {
    icon: "ri-alert-line",
    bg: "bg-foreground-100",
    iconColor: "text-foreground-700",
    dotColor: "bg-foreground-600",
  },
  workflow_started: {
    icon: "ri-refresh-line",
    bg: "bg-primary-50",
    iconColor: "text-primary-500",
    dotColor: "bg-primary-500",
  },
  workflow_step_completed: {
    icon: "ri-checkbox-circle-line",
    bg: "bg-secondary-50",
    iconColor: "text-secondary-500",
    dotColor: "bg-secondary-500",
  },
  workflow_step_skipped: {
    icon: "ri-forbid-2-line",
    bg: "bg-background-100",
    iconColor: "text-foreground-500",
    dotColor: "bg-foreground-400",
  },
  workflow_step_reopened: {
    icon: "ri-restart-line",
    bg: "bg-accent-50",
    iconColor: "text-accent-600",
    dotColor: "bg-accent-600",
  },
  workflow_step_overdue: {
    icon: "ri-alarm-warning-line",
    bg: "bg-accent-50",
    iconColor: "text-accent-600",
    dotColor: "bg-accent-600",
  },
  workflow_completed: {
    icon: "ri-flag-2-line",
    bg: "bg-secondary-50",
    iconColor: "text-secondary-600",
    dotColor: "bg-secondary-600",
  },
  renewal_decision: {
    icon: "ri-gavel-line",
    bg: "bg-primary-50",
    iconColor: "text-primary-600",
    dotColor: "bg-primary-600",
  },
};

const actionLabels: Record<AuditActionType, string> = {
  created: "Created",
  alert_sent: "Alert",
  alert_acknowledged: "Acknowledged",
  status_change: "Status Change",
  document_uploaded: "Document",
  note_added: "Note",
  escalation: "Escalation",
  workflow_started: "Workflow Started",
  workflow_step_completed: "Stage Completed",
  workflow_step_skipped: "Stage Skipped",
  workflow_step_reopened: "Stage Reopened",
  workflow_step_overdue: "Stage Overdue",
  workflow_completed: "Workflow Completed",
  renewal_decision: "Renewal Decision",
};

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("en-ZA", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDateOnly(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface AuditTimelineProps {
  entries: AuditEntry[];
}

export default function AuditTimeline({ entries }: AuditTimelineProps) {
  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-foreground-400">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-history-line text-2xl text-foreground-300"></i>
        </div>
        <p className="text-sm font-medium">No audit entries yet</p>
        <p className="text-xs mt-0.5">Actions performed on this contract will appear here</p>
      </div>
    );
  }

  const sorted = [...entries].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-[19px] top-3 bottom-3 w-px bg-background-200"></div>

      <div className="space-y-0">
        {sorted.map((entry, idx) => {
          const cfg = actionConfig[entry.actionType];
          const isLast = idx === sorted.length - 1;
          const label = actionLabels[entry.actionType];
          const isSystem = entry.performedBy === "System";

          return (
            <div key={entry.id} className={`flex gap-4 relative ${isLast ? "" : "pb-5"}`}>
              {/* Timeline dot */}
              <div className="relative z-10 shrink-0 mt-0.5">
                <div className={`w-[38px] h-[38px] rounded-full ${cfg.bg} border-2 border-white flex items-center justify-center`}>
                  <i className={`${cfg.icon} ${cfg.iconColor} text-base`}></i>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0 pt-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                    entry.actionType === "escalation"
                      ? "bg-foreground-100 text-foreground-700"
                      : entry.actionType === "alert_sent"
                      ? "bg-primary-50 text-primary-700"
                      : entry.actionType === "alert_acknowledged"
                      ? "bg-secondary-50 text-secondary-700"
                      : entry.actionType === "status_change"
                      ? "bg-accent-50 text-accent-700"
                      : "bg-background-200 text-foreground-500"
                  }`}>
                    {label}
                  </span>
                  <span className="text-foreground-400 text-xs">{formatDateOnly(entry.createdAt)}</span>
                  <span className="text-foreground-300 text-xs">{formatDateTime(entry.createdAt).split(", ")[1]}</span>
                </div>

                <p className="text-foreground-800 text-sm font-medium mt-1.5 leading-snug">{entry.action}</p>

                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs font-medium ${isSystem ? "text-foreground-400" : "text-foreground-600"}`}>
                    {entry.performedBy}
                  </span>
                  {isSystem && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-background-200 text-foreground-400">
                      SYSTEM
                    </span>
                  )}
                </div>

                {entry.notes && (
                  <div className="mt-2 p-3 bg-background-50 rounded-md border border-background-200">
                    <p className="text-foreground-500 text-xs leading-relaxed">{entry.notes}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}